import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
  debounce,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";
import { axiosWithAuth } from "../plugins/axios";
import { useNavigate } from "react-router-dom";
import { useRole } from "../lib/contexts/RoleContext";
import { getAPIUrlByRole } from "../lib/utils/config";
import DownloadIcon from "@mui/icons-material/Download";
import { StringLiteral } from "typescript";
import saveAs from "file-saver";
import Papa from "papaparse";
import to from "await-to-js";
interface FailedEvaluation {
  assetId: string;
  assetName: string;
  evaluationId: string;
  studentId: string;
  studentName: string;
  cohortId: string;
  programName: string;
  failureReason: string;
  submissionFile: string;
  quizName: string;
  quizId: string;
  submissionFileUrl: string;
  createdAt: string;
  version: string;
}

const EvaluationFailedReport = () => {
  const [failedEvaluations, setFailedEvaluations] = useState<
    FailedEvaluation[]
  >([]);
  const [allFailedEvaluations, setAllFailedEvaluations] = useState<
    FailedEvaluation[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const navigate = useNavigate();
  const role = useRole();
  const apiUrl = getAPIUrlByRole(role);

  useEffect(() => {
    const fetchFailedEvaluations = async () => {
      setError(null);
      try {
        const filters =
          filterModel.items.map((item) => ({
            column: item.field,
            value: item.value,
            operator: item.operator,
          })) || [];

        const response = await axiosWithAuth(
          `${apiUrl}autoGrade/dashboard/failedEvaluations`,
          {
            params: {
              page: page + 1,
              limit: rowsPerPage,
              sortColumn: sortModel[0]?.field || null,
              sortDirection: sortModel[0]?.sort || null,
              filters: JSON.stringify(filters),
            },
          },
          "get"
        );
        setFailedEvaluations(response.data?.data || []);
        // console.log(response?.data?.failedEvaluations)
        setTotalCount(response.data?.totalCount || 0);
        // console.log(response?.data?.totalCount)

        const allResponse = await axiosWithAuth(
          `${apiUrl}autoGrade/dashboard/allFailedEvaluations`,
          {},
          "get"
        );
        setAllFailedEvaluations(allResponse.data?.data || []);
      } catch (err) {
        setError("Failed to load evaluation failures");
      } finally {
        setLoading(false);
      }
    };

    fetchFailedEvaluations();
  }, [apiUrl, page, rowsPerPage, sortModel, filterModel]);

  const debounceFetchFailedEvaluations = useMemo(
    () =>
      debounce((filter) => {
        setFilterModel(filter);
      }, 1000),
    [filterModel]
  );

  const getFileTypeInfo = (failureReason: string): string => {
    const invalidExtensionMatch = failureReason.match(
      /has an invalid extension: (\.\w+). Expected: (\[['"]?(\.\w+)['"]?\])/
    );
    if (invalidExtensionMatch) {
      const givenType = invalidExtensionMatch[1];
      const expectedType = invalidExtensionMatch[3];
      return `Expected ${expectedType} but given ${givenType}`;
    }

    const missingFileMatch = failureReason.match(
      /ZIP file is missing \d+ files with extension (\.\w+)/
    );
    if (missingFileMatch) {
      const expectedType = missingFileMatch[1];
      return `ZIP missing required ${expectedType} file`;
    }

    return failureReason.length > 50
      ? `${failureReason.substring(0, 50)}...`
      : failureReason;
  };

  //   const exportEvaluations = () => {
  //     if (!allFailedEvaluations || allFailedEvaluations.length === 0) {
  //         console.warn("No data to export.");
  //         return;
  //     }

  //     const isPrism = role === "prism";

  //     const headers = [
  //         isPrism ? "Asset ID" : "Quiz ID",
  //         "Asset Name",
  //         "Evaluation ID",
  //         "Student ID",
  //         "Student Name",
  //         "Cohort ID",
  //         "Program Name",
  //         "Failure Reason",
  //         "Submission File URL",
  //         "Created At"
  //     ];

  //     // Ultimate CSV escaping function
  //     const escapeForCsv = (value: any): string => {
  //         if (value == null) return '""';

  //         // Convert to string and handle all special cases
  //         let str = String(value)
  //             .replace(/\\/g, '\\\\')  // Escape backslashes first
  //             .replace(/"/g, '""')     // Escape quotes
  //             .replace(/\r/g, '\\r')   // Preserve carriage returns
  //             .replace(/\n/g, '\\n');  // Convert newlines to literals

  //         // Additional handling for code snippets
  //         if (str.includes('def ') || str.includes('return ') ||
  //             str.includes('tf.') || str.includes('dtype=')) {
  //             str = str.replace(/(\r?\n|\r)/g, '\\n');
  //         }

  //         return `"${str}"`; // Always wrap in quotes
  //     };

  //     // Prepare CSV rows
  //     const csvRows = [
  //         headers.map(h => `"${h.replace(/"/g, '""')}"`).join(','),
  //         ...allFailedEvaluations.map(row => [
  //             escapeForCsv(row.quizId),
  //             escapeForCsv(row.quizName),
  //             escapeForCsv(row.evaluationId),
  //             escapeForCsv(row.studentId),
  //             escapeForCsv(row.studentName),
  //             escapeForCsv(row.cohortId),
  //             escapeForCsv(row.programName),
  //             escapeForCsv(row.failureReason), // Special handling here
  //             escapeForCsv(row.submissionFileUrl),
  //             escapeForCsv(row.createdAt)
  //         ].join(','))
  //     ];

  //     // Create CSV content
  //     const csvContent = csvRows.join('\n');
  //     const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  //     saveAs(blob, 'failedEvaluations.csv');
  // };

  // const exportEvaluations = () => {
  //   if (!allFailedEvaluations?.length) {
  //     console.warn("No data to export");
  //     return;
  //   }

  //   const isPrism = role === "prism";

  //   const rows = allFailedEvaluations.map((row) => {
  //     const processFailureReason = (text: any) => {
  //       if (!text) return "";
  //       return text
  //         .replace(/\r?\n|\r/g, "\\n")
  //         .replace(/"/g, '""')
  //         .replace(/[\t\x00-\x1F\x7F-\x9F]/g, " ");
  //     };

  //     return {
  //       [isPrism ? "Asset ID" : "Quiz ID"]: row.quizId,
  //       "Asset Name": row.quizName,
  //       Version: row.version,
  //       "Evaluation ID": row.evaluationId,
  //       "Student ID": row.studentId,
  //       "Student Name": row.studentName,
  //       "Cohort ID": row.cohortId,
  //       "Program Name": row.programName,
  //       "Failure Reason": processFailureReason(row.failureReason),
  //       "Submission File URL": row.submissionFileUrl,
  //       "Created At": row.createdAt,
  //     };
  //   });

  //   const csv = Papa.unparse(rows, {
  //     quotes: true,
  //     delimiter: ",",
  //     newline: "\n",
  //     escapeChar: '"',
  //   });

  //   const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  //   saveAs(blob, "failedEvaluations.csv");
  // };

  const processFailureReason = (text: any) => {
    if (!text) return "";
    let sanitized = String(text)
      .replace(/\r?\n|\r/g, "\\n")
      .replace(/"/g, '""')
      .replace(/[\t\x00-\x1F\x7F-\x9F]/g, " ")
      .trim();

    if (sanitized.length > 1000) {
      sanitized = sanitized.slice(0, 997) + "...";
    }

    return sanitized;
  };

  const exportEvaluations = () => {
    if (!allFailedEvaluations?.length) {
      console.warn("No data to export");
      return;
    }

    const isPrism = role === "prism";

    const rows = allFailedEvaluations.map((row) => {


      return {
        [isPrism ? "Asset ID" : "Quiz ID"]: row.quizId,
        "Asset Name": row.quizName,
        Version: row.version,
        "Evaluation ID": row.evaluationId,
        "Student ID": row.studentId,
        "Student Name": row.studentName,
        "Cohort ID": row.cohortId,
        "Program Name": row.programName,
        "Failure Reason": processFailureReason(row.failureReason),
        "Submission File URL": row.submissionFileUrl,
        "Created At": row.createdAt,
      };
    });

    const csv = Papa.unparse(rows, {
      quotes: true,
      delimiter: ",",
      newline: "\n",
      escapeChar: '"',
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "failedEvaluations.csv");
  };

  const downloadUrlCache: Record<string, string> = {};

  const handleDownloadViaAPI = async (url: string) => {
    if (!url) return;

    if (downloadUrlCache[url]) {
      window.open(downloadUrlCache[url], '_blank');
      return;
    }

    try {
      const encodedUrl = encodeURIComponent(url);
      const [err, res] = await to(
        axiosWithAuth(
          `${apiUrl}autograde/downloadFileUpload?url=${encodedUrl}`,
          {},
          'get'
        )
      );

      if (err) {
        throw new Error('Failed to fetch download link');
      }

      const downloadUrl = res.data?.downloadUrl;
      if (!downloadUrl) {
        throw new Error('No download URL returned');
      }

      downloadUrlCache[url] = downloadUrl;
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      setToastMessage((error as Error).message || 'Failed to download file');
      setToastOpen(true);
    }
  };


  const getCustomFilterOperators = () =>
    getGridStringOperators().filter(
      (operator) => operator.value === "contains" || operator.value === "equals"
    );
  const columns: GridColDef[] = [
    {
      field: "quizId",
      headerName: role === "learn" ? "Quiz ID" : "Asset ID",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.quizId ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "quizName",
      headerName: "Asset Name",
      width: 300,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.quizName ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    ...(role === "prism"
      ? [
        {
          field: "version",
          headerName: "Version",
          width: 150,
          renderCell: (params: GridRenderCellParams) =>
            params.row?.version || "-",
        },
      ]
      : []),
    {
      field: "evaluationId",
      headerName: "Evaluation ID",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.evaluationId ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "studentId",
      headerName: "Student ID",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.studentId ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "studentName",
      headerName: "Student Name",
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.studentName ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "cohortId",
      headerName: "Cohort ID",
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.cohortId ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "programName",
      headerName: "Program Name",
      width: 300,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.programName ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "failureReason",
      headerName: "Failure Reason",
      width: 400,
      renderCell: (params: GridRenderCellParams) => {
        const rawReason = params.row?.failureReason ?? "-";
        const displayText = getFileTypeInfo(rawReason);
        const trimmedTooltip =
          rawReason.split(" ").slice(0, 70).join(" ") +
          (rawReason.split(" ").length > 70 ? "..." : "");

        return (
          <Tooltip title={trimmedTooltip} placement="top" arrow>
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {displayText}
            </Box>
          </Tooltip>
        );
      },
      filterOperators: getCustomFilterOperators(),
    },
    // {
    //   field: "submissionFileUrl",
    //   headerName: "Submission File",
    //   width: 100,
    //   align: "center",
    //   headerAlign: "center",
    //   sortable: false,
    //   disableColumnMenu: true,
    //   renderCell: (params: GridRenderCellParams) => (
    //     <Tooltip title="Download Submission File" placement="top">
    //       <IconButton
    //         aria-label="download-submission"
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           window.open(params.row?.submissionFileUrl, "_blank");
    //         }}
    //         sx={{
    //           color: "black",
    //           transition: "transform 0.2s, color 0.2s",
    //           ":hover": {
    //             transform: "scale(1.2)",
    //             color: "primary.main",
    //           },
    //         }}
    //       >
    //         <DownloadIcon />
    //       </IconButton>
    //     </Tooltip>
    //   ),
    // },
    {
      field: "submissionFileUrl",
      headerName: "Submission File",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Download Submission File" placement="top">
          <IconButton
            aria-label="download-submission"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadViaAPI(params.row?.submissionFileUrl);
            }}
            sx={{
              color: "black",
              transition: "transform 0.2s, color 0.2s",
              ":hover": {
                transform: "scale(1.2)",
                color: "primary.main",
              },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      ),
    }

  ];

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom mb={4}>
        Evaluation Failed Report
      </Typography>
      <Box justifyContent={"right"} display={"flex"}>
        <Button
          variant="outlined"
          sx={{ mr: 3 }}
          onClick={exportEvaluations}
          disabled={totalCount <= 0}
        >
          {"Export Dashboard"}
        </Button>
      </Box>

      <Box mt={4}>
        <DataGridPro
          rows={failedEvaluations}
          columns={columns}
          getRowId={(row) => row._id}
          rowHeight={70}
          paginationMode="server"
          sortingMode="server"
          pagination
          paginationModel={{
            page: page,
            pageSize: rowsPerPage,
          }}
          rowCount={totalCount}
          onPaginationModelChange={(newPaginationModel) => {
            setPage(newPaginationModel.page);
            setRowsPerPage(newPaginationModel.pageSize);
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          filterMode="server"
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          onFilterModelChange={(filter) =>
            debounceFetchFailedEvaluations(filter)
          }
        />
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default EvaluationFailedReport;
